<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { Upload, X, Check, AlertCircle } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import Textarea from '@/components/admin/form/TextAreaField.vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Skeleton } from '@/components/ui/skeleton'
  import {
    OrganisationsService,
    ThemeEnum,
    type CustomerOrganisation,
    type PatchedTOrganisationRequest
  } from '@/types/generated'
  import { genericErrorToast } from '@/lib/toasts'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'

  const { t } = useTranslation({
    en: {
      title: 'Brand Identity',
      subtitle: "Customize your brand's visual identity and messaging",
      logo: 'Brand Logo',
      description: 'Brand Description',
      descriptionPlaceholder: 'Enter your brand description...',
      colorScheme: 'Color Scheme',
      chooseFile: 'Choose file',
      dragDrop: 'or drag and drop',
      removeFile: 'Remove',
      recommendedSize: 'Recommended size: 512x512px',
      maxSize: 'Maximum file size: 2MB',
      changeImage: 'Change',
      clickToUpload: 'Click to upload or drag and drop',
      selected: 'Selected:',
      invalidFileType: 'Invalid file type. Please upload an image file (PNG, JPG, GIF, or SVG).',
      fileTooLarge: 'File is too large. Maximum size is 2MB.',
      loadError: 'Failed to load branding data. Please try again.'
    },
    de: {
      title: 'Markenidentität',
      subtitle: 'Passen Sie das visuelle Erscheinungsbild und die Botschaft Ihrer Marke an',
      logo: 'Brand Logo',
      description: 'Beschreibung',
      descriptionPlaceholder: 'Geben Sie Ihre Markenbeschreibung ein...',
      colorScheme: 'Farbgestaltung',
      chooseFile: 'Datei auswählen',
      dragDrop: 'oder per Drag & Drop',
      removeFile: 'Entfernen',
      recommendedSize: 'Empfohlene Größe: 512x512px',
      maxSize: 'Maximale Dateigröße: 2MB',
      changeImage: 'Ändern',
      clickToUpload: 'Klicken oder Datei hierher ziehen',
      selected: 'Ausgewählt:',
      invalidFileType:
        'Ungültiger Dateityp. Bitte laden Sie eine Bilddatei hoch (PNG, JPG, GIF oder SVG).',
      fileTooLarge: 'Die Datei ist zu groß. Maximale Größe ist 2MB.',
      loadError: 'Markendaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.'
    }
  })

  // Color options with explicit Tailwind classes (dynamic classes get purged)
  const colorOptions = [
    { value: ThemeEnum.SLATE, bg: 'bg-slate-500', ring: 'ring-slate-500' },
    { value: ThemeEnum.GRAY, bg: 'bg-gray-500', ring: 'ring-gray-500' },
    { value: ThemeEnum.ZINC, bg: 'bg-zinc-500', ring: 'ring-zinc-500' },
    { value: ThemeEnum.NEUTRAL, bg: 'bg-neutral-500', ring: 'ring-neutral-500' },
    { value: ThemeEnum.STONE, bg: 'bg-stone-500', ring: 'ring-stone-500' },
    { value: ThemeEnum.RED, bg: 'bg-red-500', ring: 'ring-red-500' },
    { value: ThemeEnum.ORANGE, bg: 'bg-orange-500', ring: 'ring-orange-500' },
    { value: ThemeEnum.AMBER, bg: 'bg-amber-500', ring: 'ring-amber-500' },
    { value: ThemeEnum.YELLOW, bg: 'bg-yellow-500', ring: 'ring-yellow-500' },
    { value: ThemeEnum.LIME, bg: 'bg-lime-500', ring: 'ring-lime-500' },
    { value: ThemeEnum.GREEN, bg: 'bg-green-500', ring: 'ring-green-500' },
    { value: ThemeEnum.EMERALD, bg: 'bg-emerald-500', ring: 'ring-emerald-500' },
    { value: ThemeEnum.TEAL, bg: 'bg-teal-500', ring: 'ring-teal-500' },
    { value: ThemeEnum.CYAN, bg: 'bg-cyan-500', ring: 'ring-cyan-500' },
    { value: ThemeEnum.SKY, bg: 'bg-sky-500', ring: 'ring-sky-500' },
    { value: ThemeEnum.BLUE, bg: 'bg-blue-500', ring: 'ring-blue-500' },
    { value: ThemeEnum.INDIGO, bg: 'bg-indigo-500', ring: 'ring-indigo-500' },
    { value: ThemeEnum.VIOLET, bg: 'bg-violet-500', ring: 'ring-violet-500' },
    { value: ThemeEnum.PURPLE, bg: 'bg-purple-500', ring: 'ring-purple-500' },
    { value: ThemeEnum.FUCHSIA, bg: 'bg-fuchsia-500', ring: 'ring-fuchsia-500' },
    { value: ThemeEnum.PINK, bg: 'bg-pink-500', ring: 'ring-pink-500' },
    { value: ThemeEnum.ROSE, bg: 'bg-rose-500', ring: 'ring-rose-500' }
  ]

  const organisation = ref<CustomerOrganisation | null>(null)
  const isLoading = ref(true)
  const hasError = ref(false)
  const isDragging = ref(false)
  const selectedColor = ref<ThemeEnum>(ThemeEnum.VIOLET)
  const description = ref('')

  const fetchBrandingData = async () => {
    try {
      organisation.value = (await OrganisationsService.organisationsRetrieve({
        uuid: 'current'
      })) as CustomerOrganisation

      description.value = organisation.value.description || ''
      selectedColor.value = organisation.value.theme || ThemeEnum.VIOLET
    } catch (error) {
      console.error('Error fetching branding data:', error)
      hasError.value = true
      genericErrorToast()
    } finally {
      isLoading.value = false
    }
  }

  onMounted(() => {
    fetchBrandingData()
  })

  const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB
  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml']

  const validateFile = (file: File): boolean => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      genericErrorToast(t('invalidFileType'))
      return false
    }
    if (file.size > MAX_FILE_SIZE) {
      genericErrorToast(t('fileTooLarge'))
      return false
    }
    return true
  }

  const handleLogoUpload = async (event: Event) => {
    const input = event.target as HTMLInputElement
    if (input.files && input.files[0]) {
      const file = input.files[0]

      if (!validateFile(file)) return

      try {
        await OrganisationsService.organisationsUpdateLogoCreate({
          uuid: 'current',
          formData: { logo: file }
        })
        await fetchBrandingData()
      } catch (error) {
        console.error('Error uploading logo:', error)
        genericErrorToast()
      }
    }
  }

  const handleColorSelect = async (color: ThemeEnum) => {
    try {
      await OrganisationsService.organisationsPartialUpdate({
        uuid: 'current',
        requestBody: { theme: color }
      })
      selectedColor.value = color
    } catch (error) {
      console.error('Error saving theme:', error)
      genericErrorToast()
    }
  }

  const removeLogo = async () => {
    if (organisation.value?.logo) {
      try {
        await OrganisationsService.organisationsPartialUpdate({
          uuid: 'current',
          requestBody: { logo: null }
        })
        await fetchBrandingData()
      } catch (error) {
        console.error('Error removing logo:', error)
        genericErrorToast()
      }
    }
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = true
  }

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = false
  }

  const handleDrop = async (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = false

    const file = event.dataTransfer?.files[0]
    if (file && validateFile(file)) {
      try {
        await OrganisationsService.organisationsUpdateLogoCreate({
          uuid: 'current',
          formData: { logo: file }
        })
        await fetchBrandingData()
      } catch (error) {
        console.error('Error uploading logo:', error)
        genericErrorToast()
      }
    }
  }

  const handleSave = async (values: any) => {
    if (isLoading.value) return

    try {
      const formData: PatchedTOrganisationRequest = {
        description: values.description
      }

      await OrganisationsService.organisationsPartialUpdate({
        uuid: 'current',
        requestBody: formData
      })
    } catch (error) {
      console.error('Error saving branding:', error)
      genericErrorToast()
    }
  }
</script>

<template>
  <SettingsPage>
    <!-- Loading Skeleton -->
    <div
      v-if="isLoading"
      class="space-y-6 px-2 py-4 sm:px-0"
    >
      <div class="mb-6">
        <Skeleton class="mb-2 h-6 w-48" />
        <Skeleton class="h-4 w-72" />
      </div>

      <!-- Logo upload skeleton -->
      <div class="space-y-2">
        <Skeleton class="h-4 w-24" />
        <Skeleton class="h-[200px] w-full rounded-lg" />
      </div>

      <!-- Description skeleton -->
      <div class="space-y-2">
        <Skeleton class="h-4 w-32" />
        <Skeleton class="h-32 w-full" />
      </div>

      <!-- Color scheme skeleton -->
      <div class="space-y-2">
        <Skeleton class="h-4 w-28" />
        <div class="flex flex-wrap gap-3">
          <Skeleton
            v-for="i in 12"
            :key="i"
            class="h-10 w-10 rounded-lg"
          />
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <p class="text-gray-600">{{ t('loadError') }}</p>
    </div>

    <!-- Form Content -->
    <AutoSavingForm
      v-else-if="organisation"
      @save="handleSave"
    >
      <div class="px-2 py-4 sm:px-0">
        <!-- Header -->
        <div class="mb-6">
          <h2 class="text-base font-semibold leading-6 text-gray-900">{{ t('title') }}</h2>
          <p class="mt-1 text-sm text-gray-500">{{ t('subtitle') }}</p>
        </div>

        <div class="space-y-8">
          <!-- Logo Upload Section -->
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              {{ t('logo') }}
            </label>
            <div
              class="relative"
              @dragover="handleDragOver"
              @dragleave="handleDragLeave"
              @drop="handleDrop"
            >
              <div
                :class="[
                  `group relative flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-lg
                  border-2 border-dashed p-6 transition-all`,
                  isDragging
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-300 hover:border-gray-400'
                ]"
              >
                <template v-if="organisation.logo">
                  <img
                    :src="organisation.logo"
                    alt="Logo preview"
                    class="max-h-[120px] w-auto object-contain"
                  />
                  <div class="mt-4 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      variant="outline"
                      size="sm"
                      as-child
                    >
                      <label class="cursor-pointer">
                        {{ t('changeImage') }}
                        <input
                          type="file"
                          class="hidden"
                          accept="image/*"
                          @change="handleLogoUpload"
                        />
                      </label>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      @click.prevent="removeLogo"
                    >
                      <X class="mr-1.5 h-4 w-4" />
                      {{ t('removeFile') }}
                    </Button>
                  </div>
                </template>
                <template v-else>
                  <label class="flex cursor-pointer flex-col items-center">
                    <div class="mb-3 rounded-full bg-gray-100 p-3">
                      <Upload class="h-6 w-6 text-gray-400" />
                    </div>
                    <span class="text-sm text-gray-600">
                      <span class="font-medium text-purple-600 hover:text-purple-700">
                        {{ t('chooseFile') }}
                      </span>
                      {{ ' ' }}{{ t('dragDrop') }}
                    </span>
                    <input
                      type="file"
                      class="hidden"
                      accept="image/*"
                      @change="handleLogoUpload"
                    />
                  </label>
                </template>
              </div>

              <div class="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                <AlertCircle class="h-3.5 w-3.5" />
                <span>{{ t('recommendedSize') }}</span>
              </div>
            </div>
          </div>

          <!-- Description Section -->
          <div>
            <Textarea
              v-model="description"
              name="description"
              :label="t('description')"
              rows="6"
              class="resize-none"
              :placeholder="t('descriptionPlaceholder')"
            />
          </div>

          <!-- Color Selection Section -->
          <div>
            <label class="mb-3 block text-sm font-medium text-gray-700">
              {{ t('colorScheme') }}
            </label>
            <div class="flex flex-wrap gap-3">
              <button
                v-for="color in colorOptions"
                :key="color.value"
                type="button"
                :class="[
                  'relative h-10 w-10 rounded-lg shadow-sm transition-all hover:scale-110 focus:outline-none',
                  color.bg,
                  selectedColor === color.value ? `ring-2 ring-offset-2 ${color.ring}` : ''
                ]"
                @click="handleColorSelect(color.value)"
              >
                <Check
                  v-if="selectedColor === color.value"
                  class="absolute inset-0 m-auto h-5 w-5 text-white"
                />
                <span class="sr-only">{{ color.value }}</span>
              </button>
            </div>
            <p
              v-if="selectedColor"
              class="mt-3 text-sm text-gray-500"
            >
              {{ t('selected') }}
              <span class="font-medium capitalize">{{ selectedColor }}</span>
            </p>
          </div>
        </div>
      </div>
    </AutoSavingForm>
  </SettingsPage>
</template>
